// SOT:RBAC — Roles, permissions, and role-permission mappings. Single source of truth.
/**
 * RBAC Resources & Permissions
 *
 * Roles (ordered highest → lowest privilege):
 * Strada Admin → Admin → IT Admin → Executive →
 * Agent Manager → Agent Developer → Support → Tester → Agent Designer → Viewer
 *
 * Each resource defines its own set of actions.
 */
export const ROLES = {
    STRADA_ADMIN: 'strada_admin',
    ORGANIZATION_ADMIN: 'organization_admin',
    IT_ADMIN: 'it_admin',
    EXECUTIVE: 'executive',
    AGENT_MANAGER: 'editor',
    AGENT_DEVELOPER: 'agent_developer',
    SUPPORT: 'support',
    TESTER: 'tester',
    AGENT_DESIGNER: 'agent_designer',
    STYTCH_MEMBER: 'stytch_member'
};
/** Ordered highest → lowest privilege */
export const ROLE_HIERARCHY = [
    ROLES.STRADA_ADMIN,
    ROLES.ORGANIZATION_ADMIN,
    ROLES.IT_ADMIN,
    ROLES.EXECUTIVE,
    ROLES.AGENT_MANAGER,
    ROLES.AGENT_DEVELOPER,
    ROLES.SUPPORT,
    ROLES.TESTER,
    ROLES.AGENT_DESIGNER,
    ROLES.STYTCH_MEMBER
];
export const getHighestRole = (rolesList) => {
    const roleIds = new Set(rolesList.map((r) => r.role_id));
    for (const role of ROLE_HIERARCHY) {
        if (roleIds.has(role))
            return role;
    }
    return rolesList[0]?.role_id || ROLES.STYTCH_MEMBER;
};
export const ROLE_DISPLAY = {
    [ROLES.STRADA_ADMIN]: {
        label: 'Strada admin',
        description: 'Full platform access across all organizations'
    },
    [ROLES.ORGANIZATION_ADMIN]: {
        label: 'Admin',
        description: 'Full access to all organization settings and members'
    },
    [ROLES.IT_ADMIN]: {
        label: 'IT admin',
        description: 'Manages integrations, secrets, phone numbers, and widgets'
    },
    [ROLES.EXECUTIVE]: {
        label: 'Executive',
        description: 'Can create and manage analytics dashboards and reports'
    },
    [ROLES.AGENT_MANAGER]: {
        label: 'Agent manager',
        description: 'Can create and edit agents, manage campaigns, connections, environments, and phone numbers, and control agent access for developers and other managers'
    },
    [ROLES.AGENT_DEVELOPER]: {
        label: 'Agent developer',
        description: 'Can create and edit agents, campaigns, knowledge bases, analytics, and related configurations'
    },
    [ROLES.SUPPORT]: {
        label: 'Support',
        description: 'Can review conversations and claims, and manage QA, gaps, and knowledge bases'
    },
    [ROLES.TESTER]: {
        label: 'Tester',
        description: 'Can manage test numbers, run campaigns, and review conversations'
    },
    [ROLES.AGENT_DESIGNER]: {
        label: 'Agent designer',
        description: 'Can design chat templates. View-only elsewhere, with conversation feedback'
    },
    [ROLES.STYTCH_MEMBER]: { label: 'Viewer', description: 'View-only access with conversation feedback' }
};
// -- Default role→permission mappings --
/** Base read permissions shared by all roles (excludes secrets & api-keys) */
const BASE_READ_PERMISSIONS = [
    'agents:read',
    'members:read',
    'campaigns:read',
    'connections:read',
    'phone-numbers:read',
    'knowledge-bases:read',
    'qa:read',
    'schedules:read',
    'ended-reasons:read',
    'call-retry-configs:read',
    'test-numbers:read',
    'pronunciations:read',
    'sentinel:read',
    'environments:read',
    'do-not-call-list:read',
    'widgets:read',
    'conversations:read',
    'analytics:read',
    'reports:read',
    'claims:read',
    'gaps:read'
];
/** Full read permissions including secrets & api-keys */
const FULL_READ_PERMISSIONS = [...BASE_READ_PERMISSIONS, 'secrets:read', 'api-keys:read'];
const VIEWER_PERMISSIONS = [...BASE_READ_PERMISSIONS];
const AGENT_DESIGNER_PERMISSIONS = [...BASE_READ_PERMISSIONS, 'widgets:design'];
const TESTER_PERMISSIONS = [
    ...BASE_READ_PERMISSIONS,
    'test-numbers:manage',
    'campaigns:execute',
    'conversations:manage',
    'call-retry-configs:manage'
];
const SUPPORT_PERMISSIONS = [
    ...FULL_READ_PERMISSIONS,
    'conversations:manage',
    'gaps:manage',
    'qa:manage',
    'knowledge-bases:manage'
];
const AGENT_DEVELOPER_PERMISSIONS = [
    ...FULL_READ_PERMISSIONS,
    'agents:create',
    'agents:update',
    'campaigns:manage',
    'campaigns:execute',
    'knowledge-bases:manage',
    'qa:manage',
    'schedules:manage',
    'ended-reasons:manage',
    'call-retry-configs:manage',
    'do-not-call-list:manage',
    'pronunciations:manage',
    'sentinel:manage',
    'conversations:manage',
    'analytics:manage',
    'agents:develop'
];
const AGENT_MANAGER_PERMISSIONS = [
    ...AGENT_DEVELOPER_PERMISSIONS,
    'agents:manage-access',
    'connections:manage',
    'environments:manage',
    'phone-numbers:manage'
];
const EXECUTIVE_PERMISSIONS = [
    ...FULL_READ_PERMISSIONS,
    'scim:read',
    'analytics:manage',
    'reports:manage',
    'agents:develop'
];
const IT_ADMIN_PERMISSIONS = [
    ...FULL_READ_PERMISSIONS,
    'scim:read',
    'connections:manage',
    'environments:manage',
    'secrets:manage',
    'api-keys:manage',
    'phone-numbers:manage',
    'widgets:design',
    'widgets:manage',
    'scim:manage',
    'agents:develop'
];
const ORGANIZATION_ADMIN_PERMISSIONS = [
    ...FULL_READ_PERMISSIONS,
    'scim:read',
    'agents:create',
    'agents:update',
    'agents:delete',
    'members:invite',
    'members:remove',
    'members:change-role',
    'campaigns:manage',
    'campaigns:execute',
    'connections:manage',
    'environments:manage',
    'secrets:manage',
    'api-keys:manage',
    'phone-numbers:manage',
    'knowledge-bases:manage',
    'qa:manage',
    'schedules:manage',
    'ended-reasons:manage',
    'call-retry-configs:manage',
    'test-numbers:manage',
    'pronunciations:manage',
    'sentinel:manage',
    'do-not-call-list:manage',
    'widgets:design',
    'widgets:manage',
    'conversations:manage',
    'analytics:manage',
    'reports:manage',
    'gaps:manage',
    'agents:manage-access',
    'scim:manage',
    'agents:develop',
    'audit-logs:read'
];
const STRADA_ADMIN_PERMISSIONS = [...ORGANIZATION_ADMIN_PERMISSIONS, 'strada:access'];
export const ROLE_PERMISSIONS = {
    [ROLES.STYTCH_MEMBER]: VIEWER_PERMISSIONS,
    [ROLES.AGENT_DESIGNER]: AGENT_DESIGNER_PERMISSIONS,
    [ROLES.TESTER]: TESTER_PERMISSIONS,
    [ROLES.SUPPORT]: SUPPORT_PERMISSIONS,
    [ROLES.AGENT_DEVELOPER]: AGENT_DEVELOPER_PERMISSIONS,
    [ROLES.AGENT_MANAGER]: AGENT_MANAGER_PERMISSIONS,
    [ROLES.EXECUTIVE]: EXECUTIVE_PERMISSIONS,
    [ROLES.IT_ADMIN]: IT_ADMIN_PERMISSIONS,
    [ROLES.ORGANIZATION_ADMIN]: ORGANIZATION_ADMIN_PERMISSIONS,
    [ROLES.STRADA_ADMIN]: STRADA_ADMIN_PERMISSIONS
};
export const PERMISSION_GROUPS = [
    {
        resource: 'agents',
        label: 'Agents',
        permissions: [
            {
                permission: 'agents:read',
                label: 'View agents',
                description: 'Can view agents and their configurations'
            },
            { permission: 'agents:create', label: 'Create agents', description: 'Can create new agents' },
            {
                permission: 'agents:update',
                label: 'Edit agents',
                description: 'Can modify existing agent settings'
            },
            {
                permission: 'agents:manage-access',
                label: 'Manage agent access',
                description: 'Can control which agents other members can edit'
            },
            {
                permission: 'agents:develop',
                label: 'Develop agents',
                description: 'Can run test cases, simulations, and view agent versions'
            }
        ]
    },
    {
        resource: 'members',
        label: 'Members',
        permissions: [
            {
                permission: 'members:read',
                label: 'View members',
                description: 'Can view the list of organization members'
            },
            {
                permission: 'members:invite',
                label: 'Invite members',
                description: 'Can invite new members to the organization'
            },
            {
                permission: 'members:remove',
                label: 'Remove members',
                description: 'Can remove members from the organization'
            },
            {
                permission: 'members:change-role',
                label: 'Change roles',
                description: 'Can modify member roles (cannot assign roles above own level)'
            }
        ]
    },
    {
        resource: 'campaigns',
        label: 'Campaigns',
        permissions: [
            {
                permission: 'campaigns:read',
                label: 'View campaigns',
                description: 'Can view campaigns and their details'
            },
            {
                permission: 'campaigns:manage',
                label: 'Manage campaigns',
                description: 'Can create, edit, archive, and configure campaigns'
            },
            {
                permission: 'campaigns:execute',
                label: 'Run campaigns',
                description: 'Can run, pause, and resume live outbound campaigns'
            }
        ]
    },
    {
        resource: 'connections',
        label: 'Connections',
        permissions: [
            {
                permission: 'connections:read',
                label: 'View connections',
                description: 'Can view configured integrations'
            },
            {
                permission: 'connections:manage',
                label: 'Manage connections',
                description: 'Can create, delete, and test integrations'
            }
        ]
    },
    {
        resource: 'secrets',
        label: 'Secrets',
        permissions: [
            {
                permission: 'secrets:read',
                label: 'View secrets',
                description: 'Can view secrets (masked values)'
            },
            {
                permission: 'secrets:manage',
                label: 'Manage secrets',
                description: 'Can create, edit, and delete secrets'
            }
        ]
    },
    {
        resource: 'api-keys',
        label: 'API keys',
        permissions: [
            {
                permission: 'api-keys:read',
                label: 'View API keys',
                description: 'Can view API key names and metadata'
            },
            {
                permission: 'api-keys:manage',
                label: 'Manage API keys',
                description: 'Can create and delete API keys'
            }
        ]
    },
    {
        resource: 'phone-numbers',
        label: 'Phone numbers',
        permissions: [
            {
                permission: 'phone-numbers:read',
                label: 'View phone numbers',
                description: 'Can view phone numbers, groups, and coverage'
            },
            {
                permission: 'phone-numbers:manage',
                label: 'Manage phone numbers',
                description: 'Can import, edit, release phone numbers and manage groups'
            }
        ]
    },
    {
        resource: 'knowledge-bases',
        label: 'Knowledge bases',
        permissions: [
            {
                permission: 'knowledge-bases:read',
                label: 'View knowledge bases',
                description: 'Can view knowledge bases, files, and versions'
            },
            {
                permission: 'knowledge-bases:manage',
                label: 'Manage knowledge bases',
                description: 'Can create, edit, delete knowledge bases and trigger ingestion'
            }
        ]
    },
    {
        resource: 'qa',
        label: 'Quality assurance',
        permissions: [
            {
                permission: 'qa:read',
                label: 'View QA',
                description: 'Can view rubrics, sentiment labels, and QA results'
            },
            {
                permission: 'qa:manage',
                label: 'Manage QA',
                description: 'Can create, edit, delete rubrics and labels, and trigger backfill'
            }
        ]
    },
    {
        resource: 'sentinel',
        label: 'Sentinel',
        permissions: [
            {
                permission: 'sentinel:read',
                label: 'View Sentinel',
                description: 'Can view Sentinel settings and what it flagged on calls'
            },
            {
                permission: 'sentinel:manage',
                label: 'Manage Sentinel',
                description: "Can change strikes and each agent's Sentinel setting and workflow"
            }
        ]
    },
    {
        resource: 'schedules',
        label: 'Schedules',
        permissions: [
            {
                permission: 'schedules:read',
                label: 'View schedules',
                description: 'Can view business hour schedules'
            },
            {
                permission: 'schedules:manage',
                label: 'Manage schedules',
                description: 'Can create, edit, delete, and restore schedules'
            }
        ]
    },
    {
        resource: 'ended-reasons',
        label: 'Ended reasons',
        permissions: [
            {
                permission: 'ended-reasons:read',
                label: 'View ended reasons',
                description: 'Can view call ended reasons'
            },
            {
                permission: 'ended-reasons:manage',
                label: 'Manage ended reasons',
                description: 'Can create, edit, and delete custom ended reasons'
            }
        ]
    },
    {
        resource: 'call-retry-configs',
        label: 'Call retry configs',
        permissions: [
            {
                permission: 'call-retry-configs:read',
                label: 'View call retry configs',
                description: 'Can view call retry configurations'
            },
            {
                permission: 'call-retry-configs:manage',
                label: 'Manage call retry configs',
                description: 'Can create and edit call retry configurations'
            }
        ]
    },
    {
        resource: 'test-numbers',
        label: 'Test numbers',
        permissions: [
            {
                permission: 'test-numbers:read',
                label: 'View test numbers',
                description: 'Can view test phone numbers'
            },
            {
                permission: 'test-numbers:manage',
                label: 'Manage test numbers',
                description: 'Can add and delete test phone numbers'
            }
        ]
    },
    {
        resource: 'pronunciations',
        label: 'Pronunciations',
        permissions: [
            {
                permission: 'pronunciations:read',
                label: 'View pronunciations',
                description: 'Can view pronunciation dictionaries'
            },
            {
                permission: 'pronunciations:manage',
                label: 'Manage pronunciations',
                description: 'Can create, edit, and delete pronunciation dictionaries'
            }
        ]
    },
    {
        resource: 'environments',
        label: 'Environments',
        permissions: [
            {
                permission: 'environments:read',
                label: 'View environments',
                description: 'Can view environments'
            },
            {
                permission: 'environments:manage',
                label: 'Manage environments',
                description: 'Can create, edit, and delete environments'
            }
        ]
    },
    {
        resource: 'do-not-call-list',
        label: 'Do not call list',
        permissions: [
            {
                permission: 'do-not-call-list:read',
                label: 'View do not call list',
                description: 'Can view the do-not-call list and tracking status'
            },
            {
                permission: 'do-not-call-list:manage',
                label: 'Manage do not call list',
                description: 'Can import, remove entries, and toggle tracking'
            }
        ]
    },
    {
        resource: 'widgets',
        label: 'Widgets',
        permissions: [
            {
                permission: 'widgets:read',
                label: 'View widgets',
                description: 'Can view widget configurations and allowed origins'
            },
            {
                permission: 'widgets:design',
                label: 'Design chat templates',
                description: 'Can create, edit, and delete chat templates'
            },
            {
                permission: 'widgets:manage',
                label: 'Manage default and domains',
                description: 'Can set the default chat template and manage authorized domains'
            }
        ]
    },
    {
        resource: 'conversations',
        label: 'Conversations',
        permissions: [
            {
                permission: 'conversations:read',
                label: 'View conversations',
                description: 'Can view calls, chats, emails, conversation history, and submit feedback'
            },
            {
                permission: 'conversations:manage',
                label: 'Manage conversations',
                description: 'Can manage conversation views and export conversations'
            }
        ]
    },
    {
        resource: 'analytics',
        label: 'Analytics',
        permissions: [
            {
                permission: 'analytics:read',
                label: 'View analytics',
                description: 'Can view analytics dashboards and charts'
            },
            {
                permission: 'analytics:manage',
                label: 'Manage analytics',
                description: 'Can create, edit, delete analytics views and charts'
            }
        ]
    },
    {
        resource: 'reports',
        label: 'Reports',
        permissions: [
            { permission: 'reports:read', label: 'View reports', description: 'Can view configured reports' },
            {
                permission: 'reports:manage',
                label: 'Manage reports',
                description: 'Can create, edit, delete, and trigger reports'
            }
        ]
    },
    {
        resource: 'claims',
        label: 'Claims',
        permissions: [{ permission: 'claims:read', label: 'View claims', description: 'Can view claims data' }]
    },
    {
        resource: 'audit-logs',
        label: 'Audit logs',
        permissions: [
            { permission: 'audit-logs:read', label: 'View audit logs', description: 'Can view audit logs' }
        ]
    },
    {
        resource: 'gaps',
        label: 'Gaps',
        permissions: [
            { permission: 'gaps:read', label: 'View gaps', description: 'Can view gap clusters and analysis' },
            { permission: 'gaps:manage', label: 'Manage gaps', description: 'Can update gap cluster status' }
        ]
    },
    {
        resource: 'scim',
        label: 'SCIM provisioning',
        permissions: [
            { permission: 'scim:read', label: 'View SCIM', description: 'Can view SCIM connection status' },
            {
                permission: 'scim:manage',
                label: 'Manage SCIM',
                description: 'Can create, update, and delete SCIM connections'
            }
        ]
    },
    {
        resource: 'strada',
        label: 'Strada internal',
        permissions: [
            {
                permission: 'strada:access',
                label: 'Strada access',
                description: 'Access to Strada internal tools and features'
            }
        ]
    }
];
//# sourceMappingURL=rbac-resources.js.map